import { Component, Prop, Host, Element, State, Watch, h } from '@stencil/core';
import surveyProvider from './services/surveyProvider';

const BASE_URL = '/content/forms/af/';

@Component({
  tag: 'hpma-feedback-intercept',
  styleUrl: 'feedback-intercept.scss',
  shadow: true,
})
export class FeedbackIntercept {
  /**
   * Whether the modal is open or not
   */
  @Prop({ mutable: true, reflect: true }) isOpen: boolean = false;

  /**
   * The title of the modal.  Overrides the title provided by the surveyProvider.
   */
  @Prop({ mutable: true }) titleText: string;

  /**
   * The url of the survey form, relative to /content/forms/af/ on the current server.  Overrides the surveyUrl provided by the surveyProvider.
   */
  @Prop({ mutable: true }) surveyUrl: string;

  /**
   * The text of the feedback button
   */
  @Prop() buttonText: string = 'Feedback';

  /**
   * The aria label for the close button
   */
  @Prop() closeAriaLabel = 'Close';

  /**
   * The height of the content in the iframe, measured after the iframe has loaded
   */
  @State() contentHeight: number;

  @Element() host: HTMLElement;

  timeoutId: NodeJS.Timeout;

  constructor() {
    this.keyUpListener = this.keyUpListener.bind(this);
  }

  get formFrame() {
    return this.host.shadowRoot.querySelector('.survey-form') as HTMLIFrameElement;
  }

  handleClick = () => {
    this.isOpen = true;
  };

  @Watch('contentHeight')
  onContentHeightChange() {
    this.host.style.setProperty('--content-height', `${this.contentHeight}px`);
  }

  @Watch('isOpen')
  setContentHeight() {
    const { formFrame } = this;
    this.contentHeight = formFrame.contentDocument.body.scrollHeight;
  }

  closeModal(e?: CustomEvent) {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    this.isOpen = false;
  }

  keyUpListener(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.closeModal();
    }
  }

  onIframeLoad() {
    const { formFrame } = this;
    this.timeoutId = setTimeout(() => {
      this.setContentHeight();
      formFrame.contentDocument.body.addEventListener('keyup', this.keyUpListener);
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }, 1000);
  }

  async componentWillLoad() {
    if (!window.customElements.get('hp6-modal')) {
      // eslint-disable-next-line no-console
      console.error('hpma-feedback-intercept: Component library 6.x is required');
    }
    const surveyService = surveyProvider();

    if (!this.surveyUrl) {
      const survey = await surveyService({ path: window.location.pathname });
      this.surveyUrl = survey.url;
      this.titleText = survey.title;
    }
  }

  disconnectedCallback() {
    const { formFrame } = this;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    formFrame.contentDocument.body.removeEventListener('keyup', this.keyUpListener);
  }

  render() {
    const { isOpen, titleText, buttonText, surveyUrl, closeAriaLabel } = this;

    if (!surveyUrl) {
      // if no survey url is provided, do not render the component
      return;
    }

    return (
      <Host role="region" aria-label={buttonText}>
        <hp6-modal
          is-visible={isOpen}
          is-dismissible
          is-narrow
          onHp6Close={(e: CustomEvent) => this.closeModal(e)}
        >
          <hp6-focus-trap isActive={isOpen}>
            <div class="feedback-modal-content">
              <div class="modal-header">
                <hp6-heading size-selection="lg" tag-selection="h1" id="feedback-modal-title">
                  {titleText}
                </hp6-heading>

                <hp6-icon-button
                  ally-label={closeAriaLabel}
                  name="system-close"
                  type-selection="floating"
                  onClick={() => this.closeModal()}
                >
                </hp6-icon-button>
              </div>
              <hp6-horizontal-rule />
              <div class="modal-body" tabIndex={0}>
                <iframe
                  aria-labelledby="feedback-modal-title"
                  class="survey-form"
                  src={`${BASE_URL}${surveyUrl}`}
                  scrolling="no"
                  onLoad={() => this.onIframeLoad()}
                  aria-hidden={isOpen ? 'false' : 'true'}
                >
                </iframe>
              </div>
            </div>
          </hp6-focus-trap>
        </hp6-modal>
        <hp6-floating-action-button>
          <button onClick={this.handleClick} type="button">
            {buttonText}
          </button>
        </hp6-floating-action-button>
      </Host>
    );
  }
}
